// src/services/queries/club.ts
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { Club, QueryParams } from '@/model';
import { ClubService } from '../instances/club';
import { ClubConverter } from '@/converters';
import { GetParamsObject } from '@/model/query-params';

export const useClub = (params?: QueryParams, enabled = true) => {
  // extraia os campos que realmente afetam a busca
  const name = (params as any)?.name ?? '';
  const page = (params as any)?.page ?? 1;
  const limit = (params as any)?.limit ?? 20;
  const sort = (params as any)?.sort ?? 'name';
  const order = (params as any)?.order ?? 'asc';

  const query = useQuery(
    ['GET_CLUB', name, page, limit, sort, order], // <- chave estável
    async () => {
      const [err, data] = await ClubService.getClubs(params);
      if (err) throw err;
      return {
        data: ClubConverter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      };
    },
    {
      enabled, // só busca quando pedimos
      keepPreviousData: true, // mantém a página anterior enquanto carrega a próxima
      staleTime: 30_000, // evita revalidar em sequência
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  return useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
};

export const useClubByIdStable = (id?: string) => {
  return useQuery(
    ['GET_CLUB_BY_ID_STABLE', id ?? ''],
    async () => {
      if (!id) return undefined;
      const [err, data] = await ClubService.getClubById(id);
      if (err) throw err;
      return ClubConverter.toDomainFromByIdResponse(data);
    },
    {
      enabled: !!id,
      refetchOnWindowFocus: false,
      refetchOnMount: false, // não refaz ao remontar (evita flood no modal)
      staleTime: 60_000, // 1 min “fresco”
      cacheTime: 5 * 60_000, // 5 min em cache
    },
  );
};

export const useClubById = (id: string) => {
  const query = useQuery(
    ['GET_CLUB_BY_ID', id],
    async () => {
      const [err, data] = await ClubService.getClubById(id);
      if (err) {
        throw err;
      }
      return ClubConverter.toDomainFromByIdResponse(data);
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!id,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const usePostClubMutation = () => {
  return useMutation({
    mutationKey: ['POST_CLUB'],
    mutationFn: async (payload: any) => {
      const [err, data] = await ClubService.postClub(payload);
      if (err) {
        throw err;
      }
      return ClubConverter.toDomainFromPostResponse(data);
    },
  });
};

export const usePutClubMutation = () => {
  return useMutation({
    mutationKey: ['PUT_CLUB'],
    mutationFn: async ({ id, payload }: any) => {
      const [err, data] = await ClubService.putClub(id, payload);
      if (err) {
        throw err;
      }
      return ClubConverter.toDomainFromPutResponse(data);
    },
  });
};

export const useDeleteClubMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_CLUB'],
    mutationFn: async (id: string) => {
      const [err] = await ClubService.deleteClub(id);
      if (err) {
        throw err;
      }
      return;
    },
  });
};

export const useGetClubById = () => {
  return useMutation({
    mutationKey: ['GET_CLUB_BY_ID'],
    mutationFn: async (uuid: string) => {
      const [err, data] = await ClubService.getClubById(uuid);
      if (err) {
        throw err;
      }
      return ClubConverter.toDomainFromByIdResponse(data);
    },
  });
};

export const useAllClub = (isEnable: boolean) => {
  const query = useQuery(
    ['GET_CLUB'],
    async (): Promise<Club[]> => {
      let all: Club[] = [];
      let currentPage = 1;
      let totalPages = 1;
      do {
        const [err, data] = await ClubService.getClubs(GetParamsObject({}, 1, 900, 'asc', 'name'));
        if (err) throw err;
        all = [...all, ...ClubConverter.toDomainFromPaginatedResponse(data)];
        totalPages = data.meta.pages;
        currentPage++;
      } while (currentPage <= totalPages);
      return all;
    },
    {
      refetchOnWindowFocus: false,
      enabled: isEnable,
      suspense: !isEnable,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};
